import React, { useMemo, useContext } from 'react';
import { useSelector } from 'react-redux';
import {
  IProjectSpaceTypeCategory,
  IProjectSpaceTypeSubcategory,
} from 'spw-redux/modules/projects/types';

import {
  GridColumn,
  GridRow,
  ProgramSubtotal,
  CategorySubHeader,
} from 'spw-programming-projects/building-details/worksheet/styles';
import {
  CUSTOM_SUBCATEGORY,
  PRIVATE_OFFICES_SUBCATEGORY,
  WORKSTATIONS_SUBCATEGORY,
} from 'spw-constants';
import { HeaderRow, Padded, Subtitles } from 'spw-components/worksheet/styles';
import {
  FullWidthJustifyStartRow,
  JustifyEndRow,
  JustifyStartRow,
} from 'spw-shared/globalComponents/grid';
import {
  asPercentageFixed,
  asPercentageRounded,
  numberWithCommas,
} from 'spw-shared/utils';
import _, { camelCase } from 'lodash';
import { selectWorksheet } from 'spw-redux/modules/calculators/selectors';
import { WorksheetContext } from 'spw-programming-projects/building-details/worksheet/worksheet-context';
import { isWorkstationOrPrivateOfficeSubcategory } from 'spw-projects/utils';
import useUnitConversion from 'spw-components/unit-conversion/hooks';

type SubcategoryRowProps = {
  scenarioWorkSeats: number;
  category: IProjectSpaceTypeCategory;
  subcategory: IProjectSpaceTypeSubcategory;
};

const SubcategoryRow: React.FC<SubcategoryRowProps> = ({
  scenarioWorkSeats,
  category,
  subcategory,
}) => {
  const { showDecimals } = useContext(WorksheetContext);
  const worksheet = useSelector(selectWorksheet);
  const { convertToUnit, getUnit } = useUnitConversion();

  const { defaultSpaceTypeCategory } = subcategory;
  const isWorkstationOrPrivateOffice = isWorkstationOrPrivateOfficeSubcategory(
    subcategory
  );

  const testingSuffix = defaultSpaceTypeCategory.category;

  const subcategoryTotals =
    worksheet.subcategoriesTotals[defaultSpaceTypeCategory.category];

  const seats = subcategoryTotals?.seats;
  const totalCategoryUsf = worksheet.workspacesUSF;
  const usf = subcategoryTotals?.USF;

  const workSeatPercentage = useMemo(() => {
    const value = seats / scenarioWorkSeats;
    if (!_.isNaN(value) && _.isFinite(value)) {
      return showDecimals
        ? asPercentageFixed(value)
        : asPercentageRounded(value);
    }
    return showDecimals ? '0.00%' : '0%';
  }, [seats, scenarioWorkSeats, showDecimals]);

  const categoryPercentage = useMemo(() => {
    const value = usf / totalCategoryUsf;
    if (!_.isNaN(value) && _.isFinite(value)) {
      return showDecimals
        ? asPercentageFixed(value)
        : asPercentageRounded(value);
    }
    return showDecimals ? '0.00%' : '0%';
  }, [usf, totalCategoryUsf, showDecimals]);

  const subcategoryName = useMemo(() => {
    if (
      subcategory.defaultSpaceTypeCategory.category === CUSTOM_SUBCATEGORY ||
      !isWorkstationOrPrivateOffice
    )
      return subcategory.name;

    return [WORKSTATIONS_SUBCATEGORY, PRIVATE_OFFICES_SUBCATEGORY].includes(
      camelCase(subcategory.name)
    )
      ? subcategory.name.slice(0, -1)
      : subcategory.name;
  }, [
    isWorkstationOrPrivateOffice,
    subcategory.defaultSpaceTypeCategory,
    subcategory.name,
  ]);

  return (
    <>
      <GridRow className="top-header subcategory">
        <GridColumn xs={3}>
          <FullWidthJustifyStartRow>
            <CategorySubHeader
              marginLeft="29px"
              data-testid={`worksheet_subcat_row_title_${testingSuffix}`}
            >
              {subcategory.name.toUpperCase()}
            </CategorySubHeader>
          </FullWidthJustifyStartRow>
        </GridColumn>
        <GridColumn xs={1} />
        <GridColumn xs={3} className="row-end align-start">
          <ProgramSubtotal margin="8px 0px 0px 0px">
            <JustifyEndRow>
              <HeaderRow
                data-testid={`worksheet_subcat_row_value1_${testingSuffix}`}
              >
                <Padded>{numberWithCommas(seats, showDecimals, true)}</Padded>
              </HeaderRow>
            </JustifyEndRow>
            <JustifyEndRow>
              <Subtitles
                data-testid={`worksheet_subcat_row_label1_${testingSuffix}`}
              >
                <Padded>
                  <div>{subcategoryName}</div>
                  <div>Seats</div>
                </Padded>
              </Subtitles>
            </JustifyEndRow>
          </ProgramSubtotal>
        </GridColumn>
        <GridColumn xs={2} className="seat-percent-column">
          {subcategory.includeInTotalWorkSeats && (
            <ProgramSubtotal>
              <JustifyEndRow>
                <HeaderRow
                  data-testid={`worksheet_subcat_row_value2_${testingSuffix}`}
                >
                  <Padded>{workSeatPercentage}</Padded>
                </HeaderRow>
              </JustifyEndRow>
              <JustifyEndRow>
                <Subtitles
                  data-testid={`worksheet_subcat_row_label2_${testingSuffix}`}
                >
                  <Padded>
                    <div>% Mix of</div>
                    <div>Total Work Seats</div>
                  </Padded>
                </Subtitles>
              </JustifyEndRow>
            </ProgramSubtotal>
          )}
        </GridColumn>
        <GridColumn xs={3} className="row-end align-start">
          <ProgramSubtotal margin="8px 0px 0px 0px">
            <JustifyStartRow>
              <HeaderRow
                data-testid={`worksheet_subcat_row_value3_${testingSuffix}`}
              >
                <Padded>{categoryPercentage}</Padded>
              </HeaderRow>
            </JustifyStartRow>
            <JustifyEndRow>
              <Padded>
                <Subtitles
                  data-testid={`worksheet_subcat_row_label3_${testingSuffix}`}
                >
                  <div>
                    % of {category.name} {getUnit('area')}
                  </div>
                </Subtitles>
              </Padded>
            </JustifyEndRow>
          </ProgramSubtotal>
        </GridColumn>
        <GridColumn xs={1} className="row-end align-start">
          <ProgramSubtotal margin="8px 0px 0px 0px">
            <JustifyStartRow>
              <HeaderRow
                data-testid={`worksheet_subcat_row_value4_${testingSuffix}`}
              >
                <Padded>
                  {convertToUnit(usf, {
                    unitType: 'area',
                    formatter: (v) => numberWithCommas(v, showDecimals, true),
                  })}
                </Padded>
              </HeaderRow>
            </JustifyStartRow>
            <JustifyEndRow>
              <Padded>
                <Subtitles
                  data-testid={`worksheet_subcat_row_label4_${testingSuffix}`}
                >
                  {isWorkstationOrPrivateOffice ? (
                    <div>
                      {[
                        WORKSTATIONS_SUBCATEGORY,
                        PRIVATE_OFFICES_SUBCATEGORY,
                      ].includes(camelCase(subcategory.name))
                        ? `${subcategory.name.slice(0, -1)} ${getUnit('area')}`
                        : getUnit('area')}
                    </div>
                  ) : (
                    getUnit('area')
                  )}
                </Subtitles>
              </Padded>
            </JustifyEndRow>
          </ProgramSubtotal>
        </GridColumn>
      </GridRow>
    </>
  );
};

export default SubcategoryRow;
